<template>
  <section>
    <form method="post" @submit.prevent="onSend" novalidate>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title is-centered">
            {{ formTitle }}
          </p>
        </header>

        <section class="modal-card-body">
          <form-wrapper :validator="$v.form">
            <form-group
              horizontal
              name="fly_no"
              :label="$t('attributes.fly_no')"
            >
              <b-input
                v-model="form.fly_no"
                name="fly_no"
                :disabled="isUpdate"
                :placeholder="
                  $t('message.please_input') + $t('attributes.fly_no')
                "
                expanded
              >
              </b-input>
            </form-group>

            <form-group
              horizontal
              name="effect_date"
              :label="$t('attributes.effect_date')"
            >
              <b-datepicker
                v-model="effect_date"
                :min-date="minEffectDate"
                :placeholder="
                  $t('message.please_select') + $t('attributes.effect_date')
                "
                icon="calendar-today"
                trap-focus
                @input="onSelectedEffectDate"
              ></b-datepicker>
            </form-group>

            <form-group
              horizontal
              name="airline_code"
              attribute="attributes.airline"
              :label="$t('attributes.airline')"
            >
              <b-autocomplete
                ref="airlineSelect"
                v-model="selectedAirline"
                name="airline"
                field="display_name"
                :data="airlines"
                :placeholder="
                  $t('message.please_select') + $t('attributes.airline')
                "
                :open-on-focus="true"
                clearable
                @typing="onSelectingAirline"
                @select="onSelectedAirline"
              >
                <template #default="props">
                  <div class="media">
                    <div class="media-left" style="width: 20px">
                      {{ props.option.code }}
                    </div>
                    <div class="media-content">
                      {{ props.option.name }}({{ props.option.english_name }})
                    </div>
                  </div>
                </template>
                <template #empty>
                  No results found
                </template>
              </b-autocomplete>
            </form-group>

            <form-group
              horizontal
              name="dep_city"
              attribute="attributes.departure_city"
              :label="$t('attributes.departure_city')"
            >
              <b-autocomplete
                ref="departureCitySelect"
                v-model="selectedDepartureCity"
                name="dep_city"
                field="display_name"
                :data="departureCities"
                :placeholder="
                  $t('message.please_select') + $t('attributes.departure_city')
                "
                :open-on-focus="true"
                clearable
                @typing="onSelectingDepartureCity"
                @select="onSelectedDepartureCity"
              >
                <template #default="props">
                  <div class="media">
                    <div class="media-left" style="width: 20px">
                      {{ props.option.code }}
                    </div>
                    <div class="media-content">
                      {{ props.option.name }}({{ props.option.english_name }})
                    </div>
                  </div>
                </template>
                <template #empty>
                  No results found
                </template>
              </b-autocomplete>
            </form-group>

            <form-group
              horizontal
              name="arr_city"
              attribute="attributes.arrive_city"
              :label="$t('attributes.arrive_city')"
            >
              <b-autocomplete
                ref="arriveCitySelect"
                v-model="selectedArriveCity"
                name="arr_city"
                field="display_name"
                :data="arriveCities"
                :placeholder="
                  $t('message.please_select') + $t('attributes.arrive_city')
                "
                :open-on-focus="true"
                clearable
                @typing="onSelectingArriveCity"
                @select="onSelectedArriveCity"
              >
                <template #default="props">
                  <div class="media">
                    <div class="media-left" style="width: 20px">
                      {{ props.option.code }}
                    </div>
                    <div class="media-content">
                      {{ props.option.name }}({{ props.option.english_name }})
                    </div>
                  </div>
                </template>
                <template #empty>
                  No results found
                </template>
              </b-autocomplete>
            </form-group>

            <form-group
              horizontal
              name="departure_time"
              :label="$t('attributes.departure_time')"
            >
              <b-timepicker
                v-model="form.departure_time"
                icon="clock"
                inline
                size="is-small"
                :placeholder="
                  $t('message.please_select') + $t('attributes.departure_time')
                "
              >
              </b-timepicker>
            </form-group>

            <form-group
              horizontal
              name="arrive_time"
              :label="$t('attributes.arrive_time')"
            >
              <b-timepicker
                v-model="form.arrive_time"
                icon="clock"
                inline
                size="is-small"
                :placeholder="
                  $t('message.please_select') + $t('attributes.arrive_time')
                "
              >
              </b-timepicker>
            </form-group>

            <form-group
              horizontal
              name="weekly"
              :label="$t('attributes.weekly')"
            >
              <b-field grouped group-multiline>
                <b-field style="margin-bottom: 0.5em;">
                  <p class="control">
                    <span class="button is-static">
                      {{ $t('attributes.mon') }}
                    </span>
                  </p>
                  <b-select v-model="form.mon">
                    <option value="true">飛</option>
                    <option value="false">不飛</option>
                  </b-select>
                </b-field>

                <b-field style="margin-bottom: 0.5em;">
                  <p class="control">
                    <span class="button is-static">
                      {{ $t('attributes.tue') }}
                    </span>
                  </p>
                  <b-select v-model="form.tue">
                    <option value="true">飛</option>
                    <option value="false">不飛</option>
                  </b-select>
                </b-field>

                <b-field style="margin-bottom: 0.5em;">
                  <p class="control">
                    <span class="button is-static">
                      {{ $t('attributes.wen') }}
                    </span>
                  </p>
                  <b-select v-model="form.wen">
                    <option value="true">飛</option>
                    <option value="false">不飛</option>
                  </b-select>
                </b-field>

                <b-field style="margin-bottom: 0.5em;">
                  <p class="control">
                    <span class="button is-static">
                      {{ $t('attributes.thr') }}
                    </span>
                  </p>
                  <b-select v-model="form.thr">
                    <option value="true">飛</option>
                    <option value="false">不飛</option>
                  </b-select>
                </b-field>

                <b-field style="margin-bottom: 0.5em;">
                  <p class="control">
                    <span class="button is-static">
                      {{ $t('attributes.fri') }}
                    </span>
                  </p>
                  <b-select v-model="form.fri">
                    <option value="true">飛</option>
                    <option value="false">不飛</option>
                  </b-select>
                </b-field>

                <b-field style="margin-bottom: 0.5em;">
                  <p class="control">
                    <span class="button is-static">
                      {{ $t('attributes.sat') }}
                    </span>
                  </p>
                  <b-select v-model="form.sat">
                    <option value="true">飛</option>
                    <option value="false">不飛</option>
                  </b-select>
                </b-field>

                <b-field style="margin-bottom: 0.5em;">
                  <p class="control">
                    <span class="button is-static">
                      {{ $t('attributes.sun') }}
                    </span>
                  </p>
                  <b-select v-model="form.sun">
                    <option value="true">飛</option>
                    <option value="false">不飛</option>
                  </b-select>
                </b-field>
              </b-field>
            </form-group>

            <form-group
              horizontal
              name="estimated_days"
              :label="$t('attributes.estimated_days')"
            >
              <b-select v-model="form.estimated_days">
                <option
                  v-for="option in dayOptions"
                  :value="option"
                  :key="option"
                >
                  {{ option }}
                </option>
              </b-select>
            </form-group>
          </form-wrapper>
        </section>

        <footer class="modal-card-foot">
          <b-button
            :loading="isLoading"
            class="is-fullwidth"
            @click="$parent.close()"
          >
            {{ $t('attributes.close') }}
          </b-button>
          <b-button
            :loading="isLoading"
            type="is-primary"
            class="is-fullwidth"
            native-type="submit"
          >
            {{
              this.flySchedule.id
                ? $t('attributes.update')
                : $t('attributes.add')
            }}
          </b-button>
        </footer>

        <b-loading :active.sync="isLoading" :can-cancel="false"></b-loading>
      </div>
    </form>
  </section>
</template>

<script>
import { required, maxLength } from 'vuelidate/lib/validators'
import { debounce } from 'lodash'
import { AIRLINE_API, CITY_TEMPLATES_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'FlyScheduleForm',

  props: {
    flySchedule: {
      type: Object,
      required: true
    }
  },
  data() {
    const today = this.$dayjs()
    const minDate = today.subtract(1, 'day')

    return {
      sending: false,
      isUpdate: !!this.flySchedule.id,

      minEffectDate: minDate.toDate(),
      effect_date: null,
      dayOptions: [...Array(6).keys()],

      form: this.extractFormValues(this.flySchedule),

      loadingAirlines: false,
      airlines: [],
      selectedAirline: null,

      loadingCities: false,
      departureCities: [],
      selectedDepartureCity: null,
      arriveCities: [],
      selectedArriveCity: null,

      isImageModalActive: false,
      selectedImageUrl: null
    }
  },
  watch: {
    'form.departure_time'(time) {
      this.form.departure_hour = time.getHours()
      this.form.departure_min = time.getMinutes()
    },
    'form.arrive_time'(time) {
      this.form.arrive_hour = time.getHours()
      this.form.arrive_min = time.getMinutes()
    }
  },
  validations: {
    form: {
      fly_no: { required, maxLength: maxLength(6) },
      effect_date: { required },
      airline_code: { required },
      dep_city: { required },
      arr_city: { required },
      departure_time: { required },
      departure_hour: { required },
      departure_min: { required },
      arrive_time: { required },
      arrive_hour: { required },
      arrive_min: { required },
      mon: { required },
      tue: { required },
      wen: { required },
      thr: { required },
      fri: { required },
      sat: { required },
      sun: { required },
      estimated_days: { required }
    }
  },
  computed: {
    formTitle() {
      let title = this.flySchedule.id
        ? this.$t('attributes.modify')
        : this.$t('attributes.add')

      title += this.$t('attributes.fly_schedule')

      return title
    },
    isLoading() {
      return this.sending
    }
  },
  mounted() {
    this.getData()
  },
  methods: {
    async getData() {
      await this.getAirlines(this.form.airline)
      await this.getDepartureCities(this.form.dep_city)
      await this.getArriveCities(this.form.arr_city)
    },
    extractFormValues(data) {
      return {
        id: data.id,
        fly_no: data.fly_no,
        effect_date: data.effect_date,
        airline_code: data.airline_code,
        dep_city: data.dep_city,
        arr_city: data.arr_city,
        departure_hour: data.departure_hour,
        departure_min: data.departure_min,
        arrive_hour: data.arrive_hour,
        arrive_min: data.arrive_min,
        mon: data.mon,
        tue: data.tue,
        wen: data.wen,
        thr: data.thr,
        fri: data.fri,
        sat: data.sat,
        sun: data.sun,
        estimated_days: data.estimated_days
      }
    },
    onSelectedEffectDate(date) {
      this.form.effect_date = this.$dayjs(date).format('YYYYMMDD')
    },
    async getAirlines(search) {
      const api = `${AIRLINE_API}/list`
      const params = {
        search: search
      }

      try {
        const res = await this.axios.get(api, { params })
        this.airlines = res.data.data.map(air => ({
          ...air,
          code: air.code.trim(),
          name: air.name.trim(),
          english_name: air.english_name ? air.english_name.trim() : '',
          display_name: this.formatAirlineName(air)
        }))
      } catch (e) {
        this.airlines = []

        notifyService.fetch_error('airline', e)
      }
    },
    onSelectingAirline: debounce(function(search) {
      this.getAirlines(search)
    }, 500),
    onSelectedAirline(option) {
      this.form.airline_code = option && option.code
    },

    formatCityName(city) {
      if (!city) {
        return ''
      }

      return (
        `${city.code.trim()} - ${city.name.trim()}` +
        `(${city.english_name ? city.english_name.trim() : ''})`
      )
    },
    async getDepartureCities(search) {
      const api = `${CITY_TEMPLATES_API}/list`
      const params = {
        search: search
      }

      try {
        const res = await this.axios.get(api, { params })
        this.departureCities = res.data.data.map(city => ({
          ...city,
          code: city.code.trim(),
          name: city.name.trim(),
          english_name: city.english_name ? city.english_name.trim() : '',
          display_name: this.formatCityName(city)
        }))
      } catch (e) {
        this.departureCities = []

        notifyService.fetch_error('city', e)
      }
    },
    onSelectingDepartureCity: debounce(function(search) {
      this.getDepartureCities(search)
    }, 500),
    onSelectedDepartureCity(option) {
      this.form.dep_city = option && option.code
    },

    async getArriveCities(search) {
      const api = `${CITY_TEMPLATES_API}/list`
      const params = {
        search: search
      }

      try {
        const res = await this.axios.get(api, { params })
        this.arriveCities = res.data.data.map(city => ({
          ...city,
          code: city.code.trim(),
          name: city.name.trim(),
          english_name: city.english_name ? city.english_name.trim() : '',
          display_name: this.formatCityName(city)
        }))
      } catch (e) {
        this.arriveCities = []

        notifyService.fetch_error('city', e)
      }
    },
    onSelectingArriveCity: debounce(function(search) {
      this.getArriveCities(search)
    }, 500),
    onSelectedArriveCity(option) {
      this.form.arr_city = option && option.code
    },

    async onSend() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('send', this.form)
    }
  }
}
</script>
